import { baseApi } from '@/store/api/baseApi';
import type {
  Attribute,
  AttributeListParams,
  BulkAttributeIdsPayload,
  BulkAttributeStatusPayload,
  CreateAttributePayload,
  PaginatedAttributes,
  ReorderPayload,
  UpdateAttributePayload,
} from '@/types/attribute';

function buildAttributeListQuery(params: AttributeListParams): string {
  const searchParams = new URLSearchParams();
  searchParams.set('page', String(params.page ?? 1));
  searchParams.set('limit', String(params.limit ?? 10));
  searchParams.set('sortBy', params.sortBy ?? 'sortOrder');
  searchParams.set('sortOrder', params.sortOrder ?? 'asc');

  if (params.search?.trim()) searchParams.set('search', params.search.trim());
  if (params.status) searchParams.set('status', params.status);
  if (params.dateFrom) searchParams.set('dateFrom', params.dateFrom);
  if (params.dateTo) searchParams.set('dateTo', params.dateTo);

  return searchParams.toString();
}

export const attributesApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getAttributes: builder.query<PaginatedAttributes, AttributeListParams>({
      query: (params) => ({
        url: `/attributes?${buildAttributeListQuery(params)}`,
      }),
      serializeQueryArgs: ({ endpointName, queryArgs }) =>
        `${endpointName}(${buildAttributeListQuery(queryArgs)})`,
      forceRefetch: ({ currentArg, previousArg }) =>
        buildAttributeListQuery(currentArg ?? {}) !== buildAttributeListQuery(previousArg ?? {}),
      providesTags: (result) =>
        result?.results
          ? [
              ...result.results.map(({ id }) => ({ type: 'Attributes' as const, id })),
              { type: 'Attributes', id: 'LIST' },
            ]
          : [{ type: 'Attributes', id: 'LIST' }],
    }),
    getAttribute: builder.query<Attribute, string>({
      query: (id) => `/attributes/${id}`,
      providesTags: (_result, _error, id) => [{ type: 'Attributes', id }],
    }),
    createAttribute: builder.mutation<Attribute, CreateAttributePayload>({
      query: (body) => ({ url: '/attributes/create', method: 'POST', body }),
      invalidatesTags: [{ type: 'Attributes', id: 'LIST' }],
    }),
    updateAttribute: builder.mutation<Attribute, { id: string; body: UpdateAttributePayload }>({
      query: ({ id, body }) => ({ url: `/attributes/update/${id}`, method: 'POST', body }),
      invalidatesTags: (_result, _error, { id }) => [
        { type: 'Attributes', id },
        { type: 'Attributes', id: 'LIST' },
      ],
    }),
    deleteAttribute: builder.mutation<void, string>({
      query: (id) => ({ url: `/attributes/delete/${id}`, method: 'POST' }),
      invalidatesTags: [{ type: 'Attributes', id: 'LIST' }],
    }),
    reorderAttributes: builder.mutation<Attribute[], ReorderPayload>({
      query: (body) => ({ url: '/attributes/reorder', method: 'POST', body }),
      invalidatesTags: [{ type: 'Attributes', id: 'LIST' }],
    }),
    bulkUpdateAttributeStatus: builder.mutation<{ count: number }, BulkAttributeStatusPayload>({
      query: (body) => ({ url: '/attributes/bulk-status', method: 'POST', body }),
      invalidatesTags: [{ type: 'Attributes', id: 'LIST' }],
    }),
    bulkDeleteAttributes: builder.mutation<{ count: number }, BulkAttributeIdsPayload>({
      query: (body) => ({ url: '/attributes/bulk-delete', method: 'POST', body }),
      invalidatesTags: [{ type: 'Attributes', id: 'LIST' }],
    }),
  }),
});

export const {
  useGetAttributesQuery,
  useGetAttributeQuery,
  useCreateAttributeMutation,
  useUpdateAttributeMutation,
  useDeleteAttributeMutation,
  useReorderAttributesMutation,
  useBulkUpdateAttributeStatusMutation,
  useBulkDeleteAttributesMutation,
} = attributesApi;
